import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Axios from "../../utils/axios";
import { authHeaders } from "../../utils/axios";

export const fetchCountries = createAsyncThunk(
    "location/fetchCountries",
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`/locations/countries?search=${search || ""}&page=${page || 1}&limit=${limit || 20}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveStates = createAsyncThunk(
    "location/fetchStates",
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`/locations/states/active?search=${search || ""}&page=${page || 1}&limit=${limit || 20}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveCities = createAsyncThunk(
    "location/fetchCities",
    async ({ search, page, limit, state_ids } = {}, { rejectWithValue }) => {
        try {
            let url = `/locations/cities/active?search=${search || ""}&page=${page || 1}&limit=${limit || 20}`;
            if (state_ids) url += `&state_ids=${state_ids}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveDistricts = createAsyncThunk(
    "location/fetchDistricts",
    async ({ search, page, limit, state_ids } = {}, { rejectWithValue }) => {
        try {
            let url = `/locations/districts/active?search=${search || ""}&page=${page || 1}&limit=${limit || 100}`;
            if (state_ids) url += `&state_ids=${state_ids}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveTalukas = createAsyncThunk(
    "location/fetchTalukas",
    async ({ search, page, limit, district_ids } = {}, { rejectWithValue }) => {
        try {
            let url = `/locations/talukas/active?search=${search || ""}&page=${page || 1}&limit=${limit || 100}`;
            if (district_ids) url += `&district_ids=${district_ids}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveVillages = createAsyncThunk(
    "location/fetchVillages",
    async ({ search, page, limit, taluka_ids } = {}, { rejectWithValue }) => {
        try {
            let url = `/locations/villages/active?search=${search || ""}&page=${page || 1}&limit=${limit || 100}`;
            if (taluka_ids) url += `&taluka_ids=${taluka_ids}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchActiveLocalities = createAsyncThunk(
    "location/fetchLocalities",
    async ({ search, page, limit, city_ids } = {}, { rejectWithValue }) => {
        try {
            let url = `/locations/localities/active?search=${search || ""}&page=${page || 1}&limit=${limit || 100}`;
            if (city_ids) url += `&city_ids=${city_ids}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchDuCities = createAsyncThunk(
    "location/fetchDuCities",
    async ({ search, page, limit } = {}, { rejectWithValue }) => {
        try {
            const url = `/locations/du-cities?search=${search || ""}&page=${page || 1}&limit=${limit || 20}`;
            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const locationSlice = createSlice({
    name: "location",
    initialState: {
        countries: [],
        states: [],
        cities: [],
        districts: [],
        talukas: [],
        villages: [],
        localities: [],
        duCities: [],
        isLoading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCountries.pending, (state) => { state.isLoading = true; })
            .addCase(fetchCountries.fulfilled, (state, action) => {
                state.isLoading = false;
                state.countries = action.payload.data;
            })
            .addCase(fetchCountries.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveStates.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveStates.fulfilled, (state, action) => {
                state.isLoading = false;
                state.states = action.payload.data;
            })
            .addCase(fetchActiveStates.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveCities.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveCities.fulfilled, (state, action) => {
                state.isLoading = false;
                state.cities = action.payload.data;
            })
            .addCase(fetchActiveCities.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveDistricts.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveDistricts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.districts = action.payload.data;
            })
            .addCase(fetchActiveDistricts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveTalukas.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveTalukas.fulfilled, (state, action) => {
                state.isLoading = false;
                state.talukas = action.payload.data;
            })
            .addCase(fetchActiveTalukas.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveVillages.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveVillages.fulfilled, (state, action) => {
                state.isLoading = false;
                state.villages = action.payload.data;
            })
            .addCase(fetchActiveVillages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchActiveLocalities.pending, (state) => { state.isLoading = true; })
            .addCase(fetchActiveLocalities.fulfilled, (state, action) => {
                state.isLoading = false;
                state.localities = action.payload.data;
            })
            .addCase(fetchActiveLocalities.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchDuCities.pending, (state) => { state.isLoading = true; })
            .addCase(fetchDuCities.fulfilled, (state, action) => {
                state.isLoading = false;
                state.duCities = action.payload.data;
            })
            .addCase(fetchDuCities.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    }
});

export default locationSlice.reducer;
